/**
 * GA4 ecommerce events for the online store (gtag is loaded in public/index.html
 * on every non-admin page). They show where sales come from - Google free
 * listings included - and become the conversion for Google Ads later.
 * Every call is a no-op when gtag isn't there (blocked, admin pages, tests).
 */
import { jewelleryType, opalType, pricing, productId } from "./storeTaxonomy";

function send(event, params) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", event, { currency: "AUD", ...params });
    }
  } catch (e) {
    /* analytics must never break the shop */
  }
}

/** A product as a GA4 item; ids match the Google product feed (SKU, else the database id). */
export function gaItem(product, quantity = 1) {
  const { price } = pricing(product);
  const item = {
    item_id: product.sku || productId(product),
    item_name: product.name,
    item_brand: "Umoona Opal Mine",
    item_category: jewelleryType(product) || (opalType(product) ? "Opal jewellery" : "Souvenirs & gifts"),
    price,
    quantity,
  };
  const opal = opalType(product);
  if (opal) item.item_category2 = `${opal} opal`;
  return item;
}

export function trackViewItem(product) {
  const item = gaItem(product);
  send("view_item", { value: item.price, items: [item] });
}

export function trackAddToCart(product, quantity = 1) {
  const item = gaItem(product, quantity);
  send("add_to_cart", { value: item.price * quantity, items: [item] });
}

/** cartItems: the cart's [{ product, quantity }] */
export function trackBeginCheckout(cartItems) {
  const items = cartItems.map((i) => gaItem(i.product, i.quantity));
  send("begin_checkout", { value: items.reduce((sum, i) => sum + i.price * i.quantity, 0), items });
}

/**
 * Once per paid order (the success page can be reloaded). status = the
 * /api/checkout/status response: amount, order_number, order_items.
 */
export function trackPurchase(status) {
  const id = status.order_number || status.session_id;
  const key = `ga_purchase_${id}`;
  try {
    if (window.localStorage.getItem(key)) return;
    window.localStorage.setItem(key, "1");
  } catch (e) {
    /* storage blocked: send anyway */
  }
  const items = (status.order_items || []).map((i) => ({
    item_name: i.name,
    price: Number(i.price) || 0,
    quantity: Number(i.quantity) || 1,
  }));
  const itemsTotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  send("purchase", {
    transaction_id: id,
    value: Number(status.amount) || itemsTotal,
    shipping: Math.max(0, Math.round(((Number(status.amount) || 0) - itemsTotal) * 100) / 100),
    items,
  });
}

/**
 * A paid tour booking, once per booking reference. result = the
 * /api/tours/checkout/complete response (booking_reference, total_amount,
 * tickets). Items are tour tickets, so tour and shop revenue can be told
 * apart in GA4 (item_category "Underground tour").
 */
export function trackTourPurchase(result) {
  const id = result.booking_reference;
  if (!id) return;
  const key = `ga_purchase_${id}`;
  try {
    if (window.localStorage.getItem(key)) return;
    window.localStorage.setItem(key, "1");
  } catch (e) {
    /* storage blocked: send anyway */
  }
  const items = (result.tickets || []).map((t) => ({
    item_id: `tour-${t.ticket_type}`,
    item_name: `Underground tour ticket (${String(t.ticket_type || "").replace(/_/g, " ")})`,
    item_category: "Underground tour",
    price: Number(t.price_per_ticket) || 0,
    quantity: Number(t.quantity) || 1,
  }));
  const itemsTotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  send("purchase", {
    transaction_id: id,
    value: Number(result.total_amount) || itemsTotal,
    affiliation: "Tour booking",
    items,
  });
}

/** Tour checkout started (before Stripe). tickets = the booking request's tickets. */
export function trackTourCheckout(tickets, total) {
  const items = (tickets || []).map((t) => ({
    item_id: `tour-${t.ticket_type}`,
    item_name: `Underground tour ticket (${String(t.ticket_type || "").replace(/_/g, " ")})`,
    item_category: "Underground tour",
    price: Number(t.price_per_ticket) || 0,
    quantity: Number(t.quantity) || 1,
  }));
  send("begin_checkout", { value: Number(total) || 0, items });
}
